import { defineStore } from "pinia";
import { useMateCountersStore } from "~/stores/user/mateCounters";

const CLOSED_QUESTIONS = "joszaki-closed-questions";

export const useFeedStore = defineStore("feedStore", {
  state: () => ({
    marketFeed: null,
    marketFeedLoading: false,
    marketFeedLoaded: false,
    openPersonTenderFeed: null,
    openPersonTenderFeedLoading: false,
    openPersonTenderFeedLoaded: false,
    wonPersonTenderFeed: null,
    wonPersonTenderFeedLoading: false,
    wonPersonTenderFeedLoaded: false,
    archivedPersonTenderFeed: null,
    archivedPersonTenderFeedLoading: false,
    archivedPersonTenderFeedLoaded: false,
    searchTerm: "",
    searchResults: null,
    searchLoading: false,
    questionFeed: null,
    questionFeedLoading: false,
    questionFeedLoaded: false,
    skippedQuestionProfessions: [],
    skippedQuestions: localStorage.getItem(CLOSED_QUESTIONS)
      ? JSON.parse(localStorage.getItem(CLOSED_QUESTIONS))
      : [],
    // Id of the most recently closed person tender. Used to react to a tender
    // being closed (e.g. redirecting away from its dedicated page).
    lastClosedPersonTenderId: null,
  }),
  getters: {
    questions() {
      return this.questionFeed?.questions.filter(
        (x) =>
          !this.skippedQuestions.includes(x.id) &&
          !this.skippedQuestionProfessions.includes(x.profession.id)
      );
    },
    isSearching() {
      return (this.searchTerm ?? "").trim().length >= 2;
    },
  },
  actions: {
    reset() {
      this.$reset();
    },
    notifyPersonTenderClosed(personTenderId) {
      this.lastClosedPersonTenderId = personTenderId;
    },
    updateOpenTender(newPersonTender) {
      if (this.openPersonTenderFeed?.personTenders) {
        for (const personTender of this.openPersonTenderFeed.personTenders) {
          if (personTender.id === newPersonTender.id) {
            Object.assign(personTender, newPersonTender);
            break;
          }
        }
      }
    },
    removeOpenTender(personTenderId) {
      if (this.openPersonTenderFeed?.personTenders) {
        this.openPersonTenderFeed.personTenders =
          this.openPersonTenderFeed.personTenders.filter(
            (personTender) => personTender.id !== personTenderId
          );
      }
    },
    async loadOpenPersonTenderFeed(variables = { cursor: null }) {
      if (this.openPersonTenderFeedLoaded && !variables.cursor) {
        return;
      }
      const OPEN_PERSON_TENDER_QUERY = await import(
        "@/graphql/openPersonTenderFeed.graphql"
      );
      try {
        this.openPersonTenderFeedLoading = true;
        const result = await this.$nuxt.$query(
          OPEN_PERSON_TENDER_QUERY,
          variables
        );
        this.setOpenPersonTenderFeed(result.me.openPersonTenderFeed);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      } finally {
        this.openPersonTenderFeedLoading = false;
      }
    },
    async loadWonPersonTenderFeed(variables = { cursor: null }) {
      if (this.wonPersonTenderFeedLoaded && !variables.cursor) {
        return;
      }
      const WON_PERSON_TENDER_QUERY = await import(
        "@/graphql/wonPersonTenderFeed.graphql"
      );
      try {
        this.wonPersonTenderFeedLoading = true;
        const result = await this.$nuxt.$query(
          WON_PERSON_TENDER_QUERY,
          variables
        );
        this.setWonPersonTenderFeed(result.me.wonPersonTenderFeed);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      } finally {
        this.wonPersonTenderFeedLoading = false;
      }
    },
    async loadArchivedPersonTenderFeed(variables = { cursor: null }) {
      if (this.archivedPersonTenderFeedLoaded && !variables.cursor) {
        return;
      }
      const ARCHIVED_PERSON_TENDER_QUERY = await import(
        "@/graphql/archivedPersonTenderFeed.graphql"
      );
      try {
        this.archivedPersonTenderFeedLoading = true;
        const result = await this.$nuxt.$query(
          ARCHIVED_PERSON_TENDER_QUERY,
          variables
        );
        this.setArchivedPersonTenderFeed(result.me.archivedPersonTenderFeed);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      } finally {
        this.archivedPersonTenderFeedLoading = false;
      }
    },
    mergePersonTenderFeed(existing, incoming) {
      if (!existing) {
        return incoming;
      }
      existing.cursor = incoming.cursor;
      for (const tender of incoming.personTenders) {
        if (!existing.personTenders.some((x) => x.id === tender.id)) {
          existing.personTenders.push(tender);
        }
      }
      return existing;
    },
    setOpenPersonTenderFeed(openPersonTenderFeed) {
      this.openPersonTenderFeed = this.mergePersonTenderFeed(
        this.openPersonTenderFeed,
        openPersonTenderFeed
      );
      this.openPersonTenderFeedLoaded = true;
    },
    setWonPersonTenderFeed(wonPersonTenderFeed) {
      this.wonPersonTenderFeed = this.mergePersonTenderFeed(
        this.wonPersonTenderFeed,
        wonPersonTenderFeed
      );
      this.wonPersonTenderFeedLoaded = true;
    },
    setArchivedPersonTenderFeed(archivedPersonTenderFeed) {
      this.archivedPersonTenderFeed = this.mergePersonTenderFeed(
        this.archivedPersonTenderFeed,
        archivedPersonTenderFeed
      );
      this.archivedPersonTenderFeedLoaded = true;
    },
    // Moves a person tender to the bucket carried on the returned entity
    // (Folyamatban/Megnyert/Archivált), keeping the local column lists in sync
    // without a full refetch. See stores/user/feed bucket handling (issue #1360).
    moveTenderBucket(personTender) {
      if (this.searchResults) {
        const inSearch = this.searchResults.find(
          (x) => x.id === personTender.id
        );
        if (inSearch) {
          Object.assign(inSearch, personTender);
        }
      }
      const feeds = {
        in_progress: this.openPersonTenderFeed,
        won: this.wonPersonTenderFeed,
        archived: this.archivedPersonTenderFeed,
      };
      let card = personTender;
      for (const key of Object.keys(feeds)) {
        const feed = feeds[key];
        if (!feed?.personTenders) {
          continue;
        }
        const existing = feed.personTenders.find(
          (x) => x.id === personTender.id
        );
        if (existing) {
          Object.assign(existing, personTender);
          card = existing;
        }
        feed.personTenders = feed.personTenders.filter(
          (x) => x.id !== personTender.id
        );
      }
      const targetFeed = feeds[personTender.bucket];
      if (
        targetFeed?.personTenders &&
        !targetFeed.personTenders.some((x) => x.id === card.id)
      ) {
        targetFeed.personTenders = [card, ...targetFeed.personTenders];
      }
    },
    // Merges updated fields onto an existing card in place (no column change).
    mergeTender(personTender) {
      for (const feed of [
        this.openPersonTenderFeed,
        this.wonPersonTenderFeed,
        this.archivedPersonTenderFeed,
      ]) {
        const existing = feed?.personTenders?.find(
          (x) => x.id === personTender.id
        );
        if (existing) {
          Object.assign(existing, personTender);
        }
      }
      if (this.searchResults) {
        const inSearch = this.searchResults.find(
          (x) => x.id === personTender.id
        );
        if (inSearch) {
          Object.assign(inSearch, personTender);
        }
      }
    },
    // Sync the client after a legacy closePersonTender (not_interested / not_actual
    // / lost) which lands the tender in the Archivált bucket: move it between the
    // local column lists and refresh the badge counts (issue #1360).
    handleClosedPersonTender(personTender) {
      const id = personTender?.id;
      if (personTender?.bucket) {
        this.moveTenderBucket(personTender);
      } else if (id) {
        this.removeOpenTender(id);
      }
      if (id) {
        this.notifyPersonTenderClosed(id);
      }
      useMateCountersStore().refresh();
    },
    async setBucket(personTenderId, bucket) {
      const SET_PERSON_TENDER_BUCKET = await import(
        "@/graphql/mutations/setPersonTenderBucket.graphql"
      );
      const updated = await this.$nuxt.$mutate(
        SET_PERSON_TENDER_BUCKET,
        { personTenderId, bucket },
        { showErrorToast: true }
      );
      if (updated?.id) {
        this.moveTenderBucket(updated);
        useMateCountersStore().refresh();
      }
      return updated;
    },
    async setProgress(personTenderId, progress) {
      const SET_PERSON_TENDER_PROGRESS = await import(
        "@/graphql/mutations/setPersonTenderProgress.graphql"
      );
      const result = await this.$nuxt.$mutate(
        SET_PERSON_TENDER_PROGRESS,
        { personTenderId, progress },
        { showErrorToast: true }
      );
      if (result?.personTender?.id) {
        this.mergeTender(result.personTender);
      }
      return result?.personTender;
    },
    async answerFeedback(personTenderId, question, answer) {
      const ANSWER_PERSON_TENDER_MATE_FEEDBACK = await import(
        "@/graphql/mutations/answerPersonTenderMateFeedback.graphql"
      );
      const updated = await this.$nuxt.$mutate(
        ANSWER_PERSON_TENDER_MATE_FEEDBACK,
        { personTenderId, question, answer },
        { showErrorToast: true }
      );
      if (updated?.id) {
        if (updated.bucket === "won") {
          this.moveTenderBucket(updated);
          useMateCountersStore().refresh();
        } else {
          this.mergeTender(updated);
        }
      }
      return updated;
    },
    async archiveAll() {
      const ARCHIVE_ALL_PERSON_TENDERS = await import(
        "@/graphql/mutations/archiveAllPersonTenders.graphql"
      );
      const result = await this.$nuxt.$mutate(
        ARCHIVE_ALL_PERSON_TENDERS,
        {},
        { showErrorToast: true }
      );
      const archivedCount = result?.archivedCount ?? 0;
      this.openPersonTenderFeed = null;
      this.openPersonTenderFeedLoaded = false;
      this.archivedPersonTenderFeed = null;
      this.archivedPersonTenderFeedLoaded = false;
      await this.loadOpenPersonTenderFeed();
      await useMateCountersStore().refresh();
      return archivedCount;
    },
    async searchPersonTenders(term) {
      this.searchTerm = term;
      const trimmed = (term ?? "").trim();
      if (trimmed.length < 2) {
        this.searchResults = null;
        this.searchLoading = false;
        return;
      }
      const PERSON_TENDER_SEARCH = await import(
        "@/graphql/personTenderSearch.graphql"
      );
      try {
        this.searchLoading = true;
        const result = await this.$nuxt.$query(PERSON_TENDER_SEARCH, {
          term: trimmed,
          limit: 20,
        });
        // Ignore stale responses if the term changed while we waited.
        if ((this.searchTerm ?? "").trim() === trimmed) {
          this.searchResults = result.me.personTenderSearch;
        }
      } catch (e) {
        console.error("searchPersonTenders", e);
      } finally {
        this.searchLoading = false;
      }
    },
    clearSearch() {
      this.searchTerm = "";
      this.searchResults = null;
      this.searchLoading = false;
    },
    async loadMarketFeed(variables = { cursor: null }) {
      try {
        const MARKET_QUERY = await import("@/graphql/marketQuery.graphql");
        this.marketFeedLoading = true;
        const result = await this.$nuxt.$query(MARKET_QUERY, variables);
        this.setMarketFeed(result.marketFeed);
        if (!this.marketFeed.cursor) {
          this.marketFeedLoaded = true;
        }

        return Promise.resolve(result.marketFeed);
      } catch (error) {
        console.error("loadMarketFeed", error);
        return Promise.reject(error);
      } finally {
        this.marketFeedLoading = false;
      }
    },
    async hideTender(value) {
      const tenderId = parseInt(value);
      try {
        const HIDE_TENDER = await import("./hideTender.graphql");
        const result = await this.$nuxt.$mutate(HIDE_TENDER, { tenderId });
        if (result?.ok) {
          this.marketFeed.tenders = this.marketFeed.tenders.filter(
            (x) => x.id !== value
          );
          useMateCountersStore().refresh();
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },
    setMarketFeed(marketFeed) {
      if (!this.marketFeed) {
        this.marketFeed = marketFeed;
      } else {
        this.marketFeed.cursor = marketFeed.cursor;
        for (const tender of marketFeed.tenders) {
          const matching = this.marketFeed.tenders.find(
            (x) => x.id === tender.id
          );
          if (matching) {
            continue;
          }
          this.marketFeed.tenders.push(tender);
        }
      }
    },
    async buyMarketTender(value) {
      const tenderId = parseInt(value);
      try {
        const BUY_MARKET_TENDER_MUTATION = await import(
          "./buyMarketTender.graphql"
        );
        const result = await this.$nuxt.$mutate(BUY_MARKET_TENDER_MUTATION, {
          tenderId,
        });
        if (result.personTender?.id) {
          this.marketFeed.tenders = this.marketFeed.tenders.filter(
            (x) => x.id !== value
          );

          useMateCountersStore().refresh();

          this.openPersonTenderFeed = null;
          this.openPersonTenderFeedLoaded = false;
          await this.loadOpenPersonTenderFeed();

          this.archivedPersonTenderFeed = null;
          this.archivedPersonTenderFeedLoaded = false;

          return Promise.resolve(result);
        }
      } catch (err) {
        console.error(err);
        return Promise.reject(err);
      }
    },
    async resetAndReload() {
      this.$reset();

      await Promise.all([this.loadMarketFeed(), this.loadOpenPersonTenderFeed()]);
    },
    setQuestionFeed(questionFeed) {
      if (!this.questionFeed) {
        this.questionFeed = questionFeed;
      } else {
        this.questionFeed.hasMore = questionFeed.hasMore;
        for (const question of questionFeed.questions) {
          const matching = this.questionFeed.questions.find(
            (x) => x.id === question.id
          );
          if (matching) {
            continue;
          }
          this.questionFeed.questions.push(question);
        }
      }
    },
    async loadQuestionFeed() {
      try {
        const QUESTION_FEED_QUERY = await import("./questionFeed.graphql");
        this.questionFeedLoading = true;
        const result = await this.$nuxt.$query(QUESTION_FEED_QUERY, {
          skippedIds: this.skippedQuestions,
        });
        this.setQuestionFeed(result.questionFeed);
        if (!this.questionFeed.hasMore) {
          this.questionFeedLoaded = true;
        }

        return Promise.resolve(result.questionFeed);
      } catch (error) {
        console.error("loadQuestionFeed", error);
        return Promise.reject(error);
      } finally {
        this.questionFeedLoading = false;
      }
    },
    skipQuestion(questionId) {
      this.skippedQuestions.push(questionId);
      localStorage.setItem(
        CLOSED_QUESTIONS,
        JSON.stringify(this.skippedQuestions)
      );
    },
    skipQuestionProfession(professionId) {
      this.skippedQuestionProfessions.push(professionId);
    },
  },
});
